import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, Injector, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { format } from 'date-fns';
import { Subscription, takeWhile } from 'rxjs';
import { OrbitUtils } from '../../../../../../../global/orbit/orbit-utils';
import { AppointmentPickerComponent } from '../../../../../../../shared/components/appointment-picker/appointment-picker.component';
import { templateDrivenFormsViewProviders } from '../../../../../../../shared/directives/form.directive';
import { TimeFormatPipe } from '../../../../../../../shared/pipes/time-format.pipe';
import { TimeSlotFormatPipe } from '../../../../../../../shared/pipes/timeslot-format.pipe';
import { toEnum } from '../../../../../../../shared/utils/utils';
import { GAService } from '../../../../services/ga-tags.service';
import { OrbitFormClaimService } from '../../../../services/orbit-form-claim.service';
import { PutServiceOptionResponse } from '../../../../types/put-service-option-response';
import { getErrorsFromSubmission } from '../../pipes/error-subscription';
import { BookingErrors } from './booking-form-errors';

type DateString = `${string}-${string}-${string}`;

@Component({
  selector: 'app-book-engineer',
  imports: [AppointmentPickerComponent, FormsModule],
  providers: [TimeSlotFormatPipe, TimeFormatPipe],
  templateUrl: './book-engineer.component.html',
  styleUrl: './book-engineer.component.scss',
  viewProviders: [templateDrivenFormsViewProviders],
})
export class BookEngineerComponent implements OnInit, OnDestroy {
  formValue = input<Record<string, any>>({});
  slotValue = signal<any>(undefined);
  retryCounter = signal(5);
  retrySub: Subscription | undefined = undefined;
  bookingFormError = signal<string | undefined>(undefined); // store the current status of the booking form error
  retryAttempts = signal(0);
  slotsToExclude = signal<any[]>([]);
  retryError = signal(false);
  private _destroyed = false;
  formSubmitted = input.required<boolean>();
  formValid = output<boolean>();
  valid = signal(false);
  bringForwardFlagValue = signal<boolean>(false);
  appointmentData = signal<any>({});
  initialAppointmentData = signal<any>({});
  initialAppointmentDataChange = output<any>();
  appointmentDataChange = output<any>();
  dateChange = output<any>();
  isAtMaxRetrySignal = signal(false);
  isRetryLoop = signal(false);

  retryCountDown: ReturnType<typeof setInterval> | undefined;
  reAttemptTrigger: ReturnType<typeof setTimeout> | undefined;

  slotErrorMessage = '';

  private viewModel = computed(() => {
    const latestStep = this.orbitFormClaimService.state.latestStep as PutServiceOptionResponse; // mockBooking as any as PutServiceOptionResponse; //;
    const dates = Object.values(latestStep?.slotsByDate ?? {}).flat();
    const anyAllDaySlots = dates.find((i) => i.allDay);
    const selectedSlot = this.slotValue();
    const isAllDaySelection = !!selectedSlot?.allDay;
    const selectedSlotDateLabel = selectedSlot?.date
      ? format(new Date(selectedSlot.date), 'EEEE d MMMM yyyy')
      : undefined;
    const allDaySlotTitle =
      isAllDaySelection && selectedSlotDateLabel ? `On ${selectedSlotDateLabel} - All day` : undefined;

    return {
      isAllDaySlots: !!anyAllDaySlots,
      bookingOptions: latestStep,
      availabilityStartDate: latestStep?.availabilityStartDate as DateString | undefined,
      formValue: this.formValue(),
      slotValue: this.slotValue(),
      slotsToExclude: this.slotsToExclude(),
      slotTakenError: this.bookingFormError() === BookingErrors.TimeSlotTaken,
      invalidDateError: this.bookingFormError() === BookingErrors.InvalidDate,
      unknownError: this.bookingFormError() === BookingErrors.UnknownError,
      retryError: this.retryError(),
      retryCounter: this.retryCounter(),
      submittingResource: this.orbitFormClaimService.formSubmission,
      formSubmitted: this.formSubmitted(),
      formValid: this.valid(),
      bringForwardFlagValue: this.bringForwardFlagValue(),
      appointmentData: this.appointmentData(),
      initialAppointmentData: this.initialAppointmentData(),
      isAtMaxRetrys: this.isAtMaxRetrySignal(),
      isRetryLoop: this.isRetryLoop(),
      planNumber: this.orbitFormClaimService.state.claimData.bundle.planNumber,
      isService: this.orbitFormClaimService.state.claimData.bundle.claimType === 'SERVICE',
      isAllDaySelection,
      allDaySlotTitle,
    };
  });

  get vm() {
    return this.viewModel();
  }
  constructor(
    private readonly orbitFormClaimService: OrbitFormClaimService,
    private readonly injector: Injector,
    private readonly gaService: GAService,
    private readonly timeSlotFormatPipe: TimeSlotFormatPipe,
    private readonly timeFormatPipe: TimeFormatPipe
  ) {}
  ngOnDestroy(): void {
    this._destroyed = true;
  }
  ngOnInit(): void {
    this.handleAPIErrors();
    this.handleFormValidation();
  }

  setSelectedSlot(slot: any) {
    this.slotValue.set(slot);
    this.bookingFormError.set(undefined);
  }

  setBringFowardFlag(value: boolean) {
    this.bringForwardFlagValue.set(value);
  }

  handleFormValidation(): void {
    toObservable(this.formValue, { injector: this.injector })
      .pipe(takeWhile(() => !this._destroyed))
      .subscribe((v) => {
        if (v) {
          const isValid = v['selectedSlot'] != undefined;
          this.formValid.emit(isValid);
          this.valid.set(isValid);
        }
      });
  }

  handleAPIErrors(): void {
    getErrorsFromSubmission(
      this.vm.submittingResource,
      this.injector,
      (attempts, error) => {
        this.bookingFormError.set(toEnum(BookingErrors, error?.['status']) ?? BookingErrors.UnknownError);

        if (error?.['status'] === BookingErrors.ThirdPartyCommunication) {
          // Display customer error page for RD013 errors
          OrbitUtils.sendUserToErrorPage(
            new HttpErrorResponse({
              error,
            })
          );
          return;
        }

        if (error?.['status'] === BookingErrors.TimeSlotTaken) {
          if (!this.vm.slotsToExclude.includes(this.slotValue().id)) {
            this.slotsToExclude.update((val) => {
              return [...val, this.slotValue().id];
            });
          }
          this.slotErrorMessage = `Someone has just taken  ${this.timeFormatPipe.transform(this.vm.slotValue.startTime)}-${this.timeFormatPipe.transform(this.vm.slotValue.endTime)} on ${this.timeSlotFormatPipe.transform(
            this.vm.slotValue.date
          )}.`;
          this.slotValue.set(undefined);
          return;
        }

        if (error?.['status'] === BookingErrors.ClaimAlreadyConfirmed) {
          // call getRepair data and navigate to confirmation page
          this.orbitFormClaimService.getRepairData(this.orbitFormClaimService.claimID);
          return;
        }

        if (attempts === 3) {
          console.log('Max number of attempts taken');
          this.bookingFormError.set(BookingErrors.UnknownError);
          this.formValid.emit(false);
          this.isAtMaxRetrySignal.set(true);
          return;
        }

        if (error?.['allowRetry']) {
          this.isRetryLoop.set(true);
          this.bookingFormError.set(BookingErrors.RetryError);
          this.retryError.set(true);
          this.retryCounter.set(5);

          this.retryCountDown = setInterval(() => {
            this.retryCounter.update((val) => val - 1);
          }, 1000);

          this.reAttemptTrigger = setTimeout(() => {
            this.vm.submittingResource.reload();
          }, 5000);

          return;
        }
      },
      {
        autoRetry: false,
        endpointStatusChange: () => {
          this.retryError.set(false);
          if (this.retryCountDown) {
            clearInterval(this.retryCountDown);
          }
          if (this.reAttemptTrigger) {
            clearTimeout(this.reAttemptTrigger);
          }
        },
      }
    );
  }

  sendCalendarBlockGATag() {
    this.gaService.sendGATag({
      ...this.gaService.GA_BOOK_ENGINEER.CALENDAR_BLOCK(),
      eventLabel: `Block:${this.vm.appointmentData.block} | Length:${this.vm.appointmentData.numberOfActiveDatesThisMonth}| Available:${this.appointmentData().numberOfActiveDatesThisMonth}`,
      claimsFirstAvailableDate: this.vm.appointmentData.minDate,
    });
  }

  setAppointmentData(appointmentData: any) {
    // updates appointment data on calendar page change
    this.appointmentData.set(appointmentData);
    this.appointmentDataChange.emit(appointmentData);
    this.sendCalendarBlockGATag();
  }

  setInitialAppointmentData(data: any) {
    this.initialAppointmentData.set(data);
    this.initialAppointmentDataChange.emit(data);
    this.sendPageLoadGATags();
  }

  handleDateChange(data: any) {
    this.dateChange.emit(data);
  }

  sendPageLoadGATags() {
    this.gaService.sendGATag({
      ...this.gaService.GA_BOOK_ENGINEER.PAGE_LOAD(),
      claimsFirstAvailableDate: this.initialAppointmentData().minDate,
    });
    this.gaService.sendGATag({
      ...this.gaService.GA_BOOK_ENGINEER.EVENT_FIRST_DATE(),
      eventLabel: this.initialAppointmentData().minDate,
      claimsFirstAvailableDate: this.initialAppointmentData().minDate,
    });
    this.gaService.sendGATag({
      ...this.gaService.GA_BOOK_ENGINEER.EVENT_DAYS_AVAILABLE(),
      eventLabel: this.initialAppointmentData().totalActiveDates,
      claimsFirstAvailableDate: this.initialAppointmentData().minDate,
    });
  }
}
